/**
 * Plan limits data fetching — react-query hooks for relay.tenant_plan_limits.
 *
 * relay.tenant_plan_limits is not in the generated Database types yet.
 * We use the `as never` cast pattern — the same approach used by
 * inbox_conversations in features/inbox/api.ts and social_engagements in
 * features/social/api.ts — to access relay-schema tables that the type
 * generator hasn't picked up.
 *
 * The supabase client defaults to the `relay` schema (see lib/supabase.ts),
 * so `.from("tenant_plan_limits" as never)` resolves to relay.tenant_plan_limits
 * at runtime.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { useOrganization } from "@/contexts/OrganizationContext";
import type { FeatureAddonKey } from "./types";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/** Raw shape of relay.tenant_plan_limits. */
export interface TenantPlanLimits {
  organization_id: string;
  reseller_plan_id: string | null;
  max_contacts: number | null;
  max_seats: number | null;
  max_email_sends_month: number | null;
  max_automations: number | null;
  max_sequences: number | null;
  usage_contacts: number;
  usage_seats: number;
  usage_email_sends_month: number;
  usage_automations: number;
  usage_sequences: number;
  feature_gates: Record<string, unknown> | null;
  feature_addons: Record<string, unknown> | null;
  email_period_start: string | null;
}

// ---------------------------------------------------------------------------
// Internal helper — relay-table cast (matches inbox/api.ts pattern)
// ---------------------------------------------------------------------------

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function planFrom(table: string): any {
  return supabase.from(table as never);
}

// ---------------------------------------------------------------------------
// usePlanLimits — reads the current org's plan limits row
// ---------------------------------------------------------------------------

/**
 * Fetches the `relay.tenant_plan_limits` row for the currently active org.
 * Returns null when no row exists (org has no plan restrictions → unlimited).
 *
 * The RLS policy on this table allows client members to SELECT their own row,
 * so no service-role key is needed here.
 */
export function usePlanLimits() {
  const { organizationId } = useOrganization();

  return useQuery({
    queryKey: ["plan-limits", organizationId],
    enabled: !!organizationId,
    staleTime: 5 * 60 * 1_000, // 5 min — plan limits don't change often
    queryFn: async (): Promise<TenantPlanLimits | null> => {
      if (!organizationId) return null;

      const { data, error } = await planFrom("tenant_plan_limits")
        .select(
          [
            "organization_id",
            "reseller_plan_id",
            "max_contacts", "max_seats", "max_email_sends_month",
            "max_automations", "max_sequences",
            "usage_contacts", "usage_seats", "usage_email_sends_month",
            "usage_automations", "usage_sequences",
            "feature_gates",
            "feature_addons",
            "email_period_start",
          ].join(", "),
        )
        .eq("organization_id", organizationId)
        .maybeSingle();

      if (error) throw error;
      if (!data) return null;

      const r = data as Record<string, unknown>;
      return {
        organization_id: r.organization_id as string,
        reseller_plan_id: (r.reseller_plan_id as string | null) ?? null,
        max_contacts: (r.max_contacts as number | null) ?? null,
        max_seats: (r.max_seats as number | null) ?? null,
        max_email_sends_month: (r.max_email_sends_month as number | null) ?? null,
        max_automations: (r.max_automations as number | null) ?? null,
        max_sequences: (r.max_sequences as number | null) ?? null,
        usage_contacts: (r.usage_contacts as number) ?? 0,
        usage_seats: (r.usage_seats as number) ?? 0,
        usage_email_sends_month: (r.usage_email_sends_month as number) ?? 0,
        usage_automations: (r.usage_automations as number) ?? 0,
        usage_sequences: (r.usage_sequences as number) ?? 0,
        feature_gates: (r.feature_gates as Record<string, unknown> | null) ?? null,
        feature_addons: (r.feature_addons as Record<string, unknown> | null) ?? null,
        email_period_start: (r.email_period_start as string | null) ?? null,
      };
    },
  });
}

// ---------------------------------------------------------------------------
// useSetOrgFeatureAddon — self-serve org-level addon toggle
// ---------------------------------------------------------------------------

/**
 * Toggle an opt-in addon for the current org via the
 * relay_set_org_feature_addon RPC. The RPC enforces caller is an
 * owner/admin of the target org server-side. On success, invalidates
 * the plan-limits query so useFeatureAddon consumers re-render.
 *
 * The agency-side counterpart for managing client orgs is
 * relay_agency_set_client_feature_addon, which the AgencyPlanEditor
 * surface uses.
 */
export function useSetOrgFeatureAddon() {
  const { organizationId } = useOrganization();
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async (input: {
      key: FeatureAddonKey;
      enabled: boolean;
    }): Promise<void> => {
      if (!organizationId) {
        throw new Error("No active organization");
      }
      // The Relay supabase client defaults `db.schema = "relay"`, so a
      // bare `supabase.rpc(...)` would resolve to relay.<name> and 404.
      // This RPC lives in public.* — call it through the explicit
      // public-schema client. Same pattern as features/agency/api.ts.
      const { error } = await supabase.schema("public").rpc(
        "relay_set_org_feature_addon" as never,
        {
          p_organization_id: organizationId,
          p_addon_key: input.key,
          p_enabled: input.enabled,
        } as never,
      );
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["plan-limits", organizationId] });
    },
  });
}
